import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  Button,
  Breadcrumb,
  Col,
  Form
} from "react-bootstrap";
import {
  Page,
  Spinner,
  Divider,
  ErrorMessages,
  IconButton,
  IconButtonType,
  NoData,
  DeleteConfirmation
} from "@whitespace/whitespace-ui";
import { useCallback, useEffect, useState } from "react";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import providerSchema from "modules/schemas/providerSchema";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { makeDropDownTreeAACompliant } from "modules/SyncfusionAAFixer";
import { DropDownTreeComponent } from "@syncfusion/ej2-react-dropdowns";
import AmplifyError from "components/AmplifyError/AmplifyError";
import "styles/sf-base.scss";
import "styles/sf-dropdown.scss";
import IRegion from "interfaces/IRegion";
import { useStateMachine } from "little-state-machine";
import IEditProviderRequestModel from "interfaces/Request/IEditProviderRequestModel";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const EditProvider = () => {
  const { state } = useStateMachine();
  const [provider, _] = useState<IEditProviderRequestModel>({
    ...state.editProviderRequest!
  });

  const [deleteButtonPressed, setDeleteButtonPressed] = useState<boolean>(false);
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    watch
  } = useForm<IEditProviderRequestModel>({
    resolver: yupResolver(providerSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending: getRegionsIsSending,
    errors: getRegionsErrors,
    data: getRegionsData,
    sendRequest: getRegionsSendRequest
  } = useAmplifyREST();

  const {
    isSending: editProviderIsSending,
    errors: editProviderErrors,
    statusCode: editProviderStatusCode,
    sendRequest: editProviderSendRequest
  } = useAmplifyREST();

  const {
    isSending: deleteProviderIsSending,
    errors: deleteProviderErrors,
    statusCode: deleteProviderStatusCode,
    sendRequest: deleteProviderSendRequest
  } = useAmplifyREST();

  const [aaFixDropDownTreeCompleted, setAAFixDropDownTreeCompleted] = useState<boolean>(false);

  const isSending = getRegionsIsSending || editProviderIsSending || deleteProviderIsSending || !aaFixDropDownTreeCompleted;

  const navigate = useNavigate();

  const [dropDownTreeComponent, setDropDownTreeComponent] = useState<DropDownTreeComponent>();
  const [selectedRegions, setSelectedRegions] = useState<string[] | undefined>(
    provider.regions
  );
  const [editButtonDisabled, setEditButtonDisabled] = useState(true);
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [regions, setRegions] = useState<IRegion[] | undefined>();
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  const regionFields: Object = {
    dataSource: regions,
    value: "id",
    text: "name",
    child: "nodeChild"
  };

  const regionsTreeSettings = { autoCheck: false };

  const canFormBeSubmitted = () => {
    const name = getValues("name");
    const type = getValues("type");
    const emailAddress = getValues("emailAddress");
    const active = getValues("active");
    const notes = getValues("notes");

    const regionsRemoved = provider?.regions?.filter((x: any) => !selectedRegions?.includes(x)) ?? [];
    const regionsAdded = selectedRegions?.filter((x: any) => !provider?.regions?.includes(x)) ?? [];

    const requiredFieldsEntered = name.length > 0
      && type.length > 0
      && emailAddress.length > 0
      && selectedRegions
      && selectedRegions?.length > 0;

    const atLeastOneFieldChangedValue = name !== provider.name
      || regionsRemoved?.length > 0
      || regionsAdded?.length > 0
      || (name.length > 0 && provider.name !== name)
      || (type.length > 0 && provider.type !== type)
      || (emailAddress.length > 0 && provider.emailAddress !== emailAddress)
      || (notes.length > 0 && notes !== provider.notes)
      || active !== provider.active;

    setEditButtonDisabled(
      !(requiredFieldsEntered && atLeastOneFieldChangedValue)
    );
  };

  const watchFields = watch([
    "name",
    "type",
    "emailAddress",
    "notes",
    "active"
  ]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [watchFields]);

  useEffect(() => {
    setSelectedRegions(provider?.regions);
  }, [provider, setSelectedRegions]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [selectedRegions]);

  const dropDownTreeComponentOnChange = () => {
    setSelectedRegions(dropDownTreeComponent?.value);
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
    setApiErrors([]);
  }, [setShowExceptions, setApiErrors]);

  useEffect(() => {
    getRegionsSendRequest("settings/regions", "GET");
  }, [getRegionsSendRequest]);

  useEffect(() => {
    if (getRegionsData) {
      setRegions(getRegionsData.regions);
    }
  }, [getRegionsData]);

  useEffect(() => {
    if (getRegionsErrors) {
      setApiErrors(current => [...current, ...getRegionsErrors]);
      setShowExceptions(true);
      setAAFixDropDownTreeCompleted(true);
    }
  }, [getRegionsErrors, setApiErrors, setShowExceptions]);

  const handleEditProviderClick = (request: IEditProviderRequestModel) => {
    setApiErrors([]);
    request.regions = selectedRegions!;
    editProviderSendRequest(`settings/providers/${provider.id}`, "PUT", request);
  };

  useEffect(() => {
    if (editProviderErrors && editProviderStatusCode !== 200) {
      setApiErrors(current => [...current, ...editProviderErrors]);
      setShowExceptions(true);
    }
  }, [
    editProviderStatusCode,
    editProviderErrors,
    setApiErrors,
    setShowExceptions
  ]);

  useEffect(() => {
    if (editProviderStatusCode === 200) {
      navigate(RoutePathsEnum.SettingsCollectors);
    }
  }, [editProviderStatusCode]);

  const handleDeleteProvider = () => {
    deleteProviderSendRequest(`settings/providers/${provider.id}`, "DELETE");
  };

  useEffect(() => {
    if (deleteProviderStatusCode === 200) {
      navigate(RoutePathsEnum.SettingsCollectors);
    }
  }, [deleteProviderStatusCode]);

  useEffect(() => {
    if (deleteProviderErrors) {
      setApiErrors(current => [...current, ...deleteProviderErrors]);
      setShowExceptions(true);
      setDeleteButtonPressed(false);
    }
  }, [
    deleteProviderErrors,
    setApiErrors,
    setShowExceptions,
    setDeleteButtonPressed
  ]);

  useEffect(() => {
    if (dropDownTreeComponent) {
      makeDropDownTreeAACompliant();
      setAAFixDropDownTreeCompleted(true);
    }
  }, [dropDownTreeComponent]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="edit-provider">
              Edit Provider
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form
          autoComplete="off"
          onSubmit={handleSubmit(handleEditProviderClick)}
        >
          <Card.Body className="no-padding">
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Settings }}
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-collectors"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.SettingsCollectors }}
                    onClick={() => navigate(RoutePathsEnum.SettingsCollectors)}
                  >
                    Collectors
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-edit-provider">
                    {`Edit ${provider?.name} Provider`}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Delete}
                  variant="danger"
                  className="right-margin-small"
                  disabled={provider.inUse || isSending}
                  aria-disabled={provider.inUse || isSending}
                  aria-label="Delete Provider"
                  type="button"
                  spin={deleteProviderIsSending}
                  onClick={() => setDeleteButtonPressed(true)}
                  data-testid="btnDelete"
                >
                  Delete
                </IconButton>
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={editButtonDisabled || editProviderIsSending}
                  aria-disabled={editButtonDisabled || editProviderIsSending}
                  aria-label="Update Provider"
                  type="submit"
                  spin={isSending}
                  data-testid="update"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(RoutePathsEnum.SettingsCollectors)}
                  data-testid="cancel"
                  aria-label="Cancel edit and back to collectors"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <Row>
              <section className="pt-2" data-testid="formElements">
                <Container fluid>
                  <Row className="d-flex mt-1 ms-1">
                    <Col xs={12}>
                      <div className="control-pane">
                        <div className="control-section" />
                        <Row className="mb-2">
                          <Form.Group as={Col} controlId="name">
                            <Form.Label
                              id="nameLabel"
                              className="horiz-form-label"
                            >
                              Name:
                            </Form.Label>
                            <Col>
                              <Form.Control
                                type="text"
                                role="textbox"
                                defaultValue={provider?.name}
                                aria-required="true"
                                aria-invalid={ariaInvalidCheck(
                                  "name",
                                  errors,
                                  null
                                )}
                                aria-labelledby="nameLabel"
                                maxLength={50}
                                {...register("name")}
                              />
                            </Col>
                            <ErrorMessages
                              fieldName="name"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Col} controlId="type">
                            <Form.Label
                              id="typeLabel"
                              className="horiz-form-label"
                            >
                              Type:
                            </Form.Label>
                            <Col>
                              <Form.Control
                                type="text"
                                role="textbox"
                                defaultValue={provider?.type}
                                aria-required="true"
                                aria-invalid={ariaInvalidCheck(
                                  "type",
                                  errors,
                                  null
                                )}
                                aria-labelledby="typeLabel"
                                maxLength={50}
                                {...register("type")}
                              />
                            </Col>
                            <ErrorMessages
                              fieldName="type"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Col} controlId="emailAddress">
                            <Form.Label
                              id="emailAddressLabel"
                              className="horiz-form-label"
                            >
                              Email:
                            </Form.Label>
                            <Col>
                              <Form.Control
                                type="text"
                                role="textbox"
                                defaultValue={provider?.emailAddress}
                                aria-required="true"
                                aria-invalid={ariaInvalidCheck(
                                  "emailAddress",
                                  errors,
                                  null
                                )}
                                aria-labelledby="emailAddressLabel"
                                maxLength={50}
                                {...register("emailAddress")}
                              />
                            </Col>
                            <ErrorMessages
                              fieldName="emailAddress"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Col} controlId="regions">
                            <Form.Label
                              id="regionsLabel"
                              className="horiz-form-label"
                            >
                              Region(s):
                            </Form.Label>
                            <Col>
                              {regions && (
                                <DropDownTreeComponent
                                  {...register("regions")}
                                  ref={(d: DropDownTreeComponent) => {
                                    setDropDownTreeComponent(d);
                                  }}
                                  value={provider?.regions}
                                  change={dropDownTreeComponentOnChange}
                                  treeSettings={regionsTreeSettings}
                                  fields={regionFields}
                                  allowMultiSelection
                                  showCheckBox
                                  changeOnBlur={false}
                                  mode="Box"
                                  id="regionsDropDown"
                                  htmlAttributes={{
                                    "data-testid": "ddlRegions",
                                    "aria-required": "true"
                                  }}
                                />
                              )}
                              <NoData
                                visible={
                                  !apiErrors && !getRegionsIsSending && !regions
                                }
                                description="No Regions Found"
                              />
                            </Col>
                            <ErrorMessages
                              fieldName="regions"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Col} controlId="notes">
                            <Form.Label
                              id="notesLabel"
                              className="horiz-form-label"
                            >
                              Notes:
                            </Form.Label>
                            <Col>
                              <Form.Control
                                type="text"
                                role="textbox"
                                defaultValue={provider?.notes}
                                aria-required="true"
                                aria-invalid={ariaInvalidCheck(
                                  "notes",
                                  errors,
                                  null
                                )}
                                aria-labelledby="notesLabel"
                                maxLength={50}
                                {...register("notes")}
                              />
                            </Col>
                            <ErrorMessages
                              fieldName="notes"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2 mt-3">
                          <Form.Group
                            as={Col}
                            controlId="active"
                            className="d-flex align-items-center"
                          >
                            <Form.Label
                              id="activeLabel"
                              className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                            >
                              Active:
                            </Form.Label>
                            <Form.Check
                              {...register("active")}
                              type="switch"
                              className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                              aria-required="true"
                              aria-labelledby="activeLabel"
                              defaultChecked={provider?.active}
                            />
                            <ErrorMessages
                              fieldName="active"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </section>

            </Row>
          </Card.Body>
        </Form>
      </Card>
      <DeleteConfirmation
        title="Delete Provider"
        data-testid="deleteConfirmation"
        onDelete={() => {
          handleDeleteProvider();
        }}
        onHide={() => {
          setDeleteButtonPressed(false);
        }}
        isDeleting={deleteProviderIsSending}
        show={deleteButtonPressed}
      >
        <p>
          Are you sure you want to delete
          {" "}
          {provider.name}
          {" "}
          Provider?
        </p>
      </DeleteConfirmation>
    </Page>
  );
};
export default EditProvider;
